import React, { useState } from 'react';
import { Box, Button, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMsal } from '@azure/msal-react';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { loginRequest } from '../auth/authConfig';
import loginWithCognito from '../auth/cognitoAuth';
import azureLogo from '../images/logoAzure.png';
import awsLogo from '../images/logoAWS.png';
import { AWS_CONFIG_TYPE } from '../BaseUrl';
import './LoginForm.css';
import nagarroLogo1 from '../assets/images/nagarro_tr.png';

export default function LoginForm() {
  const [loading, setLoading] = useState(false);
  const { instance } = useMsal();
  const { enqueueSnackbar } = useSnackbar();

  const handleLogin = () => {
    if (AWS_CONFIG_TYPE) {
      loginWithCognito();     // AWS
    } else {
      handleAzureLogin();    // Azure
    }
  };


  /* Full-page redirect: same window returns from Microsoft (no second window; avoids ERR_CONNECTION_REFUSED). */
  const handleAzureLogin = () => {
    setLoading(true);
    instance
      .loginRedirect(loginRequest)
      .catch((err) => {
        setLoading(false);
        enqueueSnackbar('Azure AD login failed', { variant: 'error' });
        console.error('Azure loginRedirect:', err);
      });
  };

  /* ================= UI ================= */
  return (
    <>
      <Box
        sx={{
          zIndex: 10,
          width: { xs: '90%', sm: 380 },
          textAlign: 'center',
          /* 🌫️ GLASS EFFECT */
          background: 'linear-gradient(135deg, rgba(255,255,255,0.75), rgba(255,255,255,0.55))',
          backdropFilter: 'blur(18px) saturate(180%)',
          WebkitBackdropFilter: 'blur(18px) saturate(180%)',

          borderRadius: '18px',
          padding: '28px',

          /* ✨ GLASS EDGE */
          border: '1px solid rgba(255,255,255,0.35)',
          borderTop: '4px solid #1A73E8',

          /* 🌑 DEPTH */
          boxShadow: `
          0 8px 32px rgba(0,0,0,0.25),
          inset 0 1px 0 rgba(255,255,255,0.3)
        `,
        }}
      >

        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 0.8,
            px: 1,
            py: 0.4,
            borderRadius: '20px',
            background: 'rgba(255,255,255,0.85)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            fontSize: '16px',
            fontWeight: 600,
          }}
        >
          <img
            src={AWS_CONFIG_TYPE ? awsLogo : azureLogo}
            width={AWS_CONFIG_TYPE ? 20 : 40}
            alt="cloud"
          />
        </Box>


        {/* Header */}
        <Typography variant="h5" fontWeight={600}>
          Welcome Back
        </Typography>

        <Typography
          sx={{
            mt: 1,
            mb: 3,
            fontSize: '14px',
            color: 'rgba(20, 20, 20, 0.85)',   // 👈 adaptive dark
          }}
        >
          Access your intelligent data pipeline platform with AI-powered transformation and analytics
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {/* ================= AZURE BUTTON ================= */}
        <Button
          fullWidth
          onClick={handleLogin}
          disabled={loading}
          sx={{
            py: 1.6,
            fontSize: '15px',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: '50px',
            background: 'linear-gradient(135deg, #1A73E8, #0A3CC1)',
            color: '#eee9e9ff',
            boxShadow: '0 6px 20px rgba(26,115,232,0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1557B0, #082A8F)',
              boxShadow: '0 8px 26px rgba(26,115,232,0.5)',
              transform: 'translateY(-2px)',
            },
          }}
        >
          <img
            src={nagarroLogo1}
            alt="Azure"
            width={26}
            style={{ marginRight: 10 }}
          />
          {loading ? 'Signing in…' : 'Login with Organization SSO'}
        </Button>

        {/* Footer */}

        <Box
          sx={{
            mt: 3,
            p: 2,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.2,

            background: 'rgba(201, 191, 191, 0.66)',
            backdropFilter: 'blur(8px)',
            borderRadius: '12px',

            border: '1px solid rgba(0,0,0,0.06)',
          }}
        >
          <Box
            component="svg"
            viewBox="0 0 24 24"
            sx={{
              width: 18,
              height: 18,
              mt: '2px',
              fill: '#1A73E8',
              flexShrink: 0,
            }}
          >
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
          </Box>

          <Typography
            sx={{
              fontSize: '12.5px',
              lineHeight: 1.6,
              color: 'rgba(20,20,20,0.75)',
            }}
          >
            Your credentials are secured with enterprise grade authentication.
          </Typography>
        </Box>
      </Box>
      <div style={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000 }}>
        <Tooltip title="Open Tutorials" arrow>
          <IconButton onClick={() => window.open('/tutorials', '_blank')} color="primary">
            <MenuBookIcon fontSize="large" />
          </IconButton>
        </Tooltip>
      </div>
    </>
  );
}
